import {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
  useMemo,
} from 'react';
import { AppContext } from '../components/FaqOoi/Widget/SettingsProvider/SettingsProvider';

export interface UsePaginatedQuestionsProps {
  bottomRef: React.RefObject<HTMLDivElement>;
}

export interface PaginationState {
  cursor: string | null;
  loading: boolean;
  hasMore: boolean;
}

export const usePaginationQuestions = ({
  bottomRef,
}: UsePaginatedQuestionsProps) => {
  const fetchingNewQuestions = useRef(true);
  const {
    selectedCategory,
    listQuestionsResponse,
    appAPI: { getListQuestions: getListQuestionsAPI },
  } = useContext(AppContext);
  const listQuestionsResponseJSON = useMemo(
    () => JSON.stringify(listQuestionsResponse?.questionEntries),
    [listQuestionsResponse?.questionEntries],
  );
  const [pagination, setPagination] = useState<PaginationState>({
    cursor: null,
    loading: false,
    hasMore: false,
  });

  const getListQuestions = useCallback(
    async ({
      categoryId,
      cursor,
    }: {
      categoryId?: typeof selectedCategory;
      cursor?: typeof pagination.cursor;
    }) => {
      if (pagination.loading) {
        return;
      }
      await getListQuestionsAPI({
        categoryId,
        prev: JSON.parse(listQuestionsResponseJSON),
        cursor,
      });
    },
    [getListQuestionsAPI, listQuestionsResponseJSON, pagination],
  );

  useEffect(() => {
    if (!listQuestionsResponse) {
      return;
    }

    setPagination({
      cursor: listQuestionsResponse.pagingMetadata?.cursors?.next || null,
      loading: false,
      hasMore: listQuestionsResponse.pagingMetadata?.hasNext || false,
    });
    fetchingNewQuestions.current = false;
  }, [listQuestionsResponse]);
  // When need to load more questions
  const loadMoreQuestions = useCallback(async () => {
    if (!pagination.cursor || pagination.loading || !pagination.hasMore) {
      return;
    }

    setPagination((prev) => ({ ...prev, loading: true }));

    try {
      await getListQuestions({
        cursor: pagination.cursor,
      });
    } catch (error) {
      console.error('Error loading more questions:', error);
      setPagination((prev) => ({ ...prev, loading: false }));
    }
  }, [pagination, getListQuestions]);

  useEffect(() => {
    const currentBottomRef = bottomRef.current;
    const viewportHeight = window.innerHeight;
    const rootMarginValue = `${viewportHeight * 0.6}px`;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !pagination.loading &&
          pagination.hasMore
        ) {
          loadMoreQuestions();
        }
      },
      { rootMargin: rootMarginValue },
    );

    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }

    return () => {
      if (currentBottomRef) {
        observer.unobserve(currentBottomRef);
      }
    };
  }, [pagination.loading, pagination.hasMore, loadMoreQuestions, bottomRef]);

  return { pagination };
};
